// CHECK THIS FILE
import {
  AdminDatePickerInputModal,
  AdminInput,
  AdminRadioInput,
  Button,
  createMenuSchedule,
  dateTimeInitial,
  Div,
  ErrorToast,
  H5,
  Modal,
  PropsModal,
  Separator,
  SuccessToast,
  updateMenuSchedule,
  UpdateMenuScheduleV2Dto,
  WeekDay,
  reportError,
} from '@vgrubs/components';
import {
  daysOfWeekStringV2,
  MenuAvailabilityHourV2Response,
  MenuAvailabilityV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { formatDateToTime24h } from '../../../../helpers/time-helpers';
import { useSelectedMenu } from '../../menuSelection';

export const AddEditAvailability = ({
  modalIsOpen,
  closeModal,
  setLoading,
  setMenu,
  edit,
  handleChangeShouldPublish,
}: PropsModal & {
  modalIsOpen: boolean | MenuAvailabilityV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  edit?: boolean;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
}) => {
  const [name, setName] = useState('');
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<WeekDay[]>([]);

  const [timeFrom, setTimeFrom] = useState<Date[]>([dateTimeInitial]);
  const [timeTo, setTimeTo] = useState<Date[]>([dateTimeInitial]);

  const { locationId, brandId, provider } = useParams() as {
    locationId: string;
    brandId: string;
    provider: Provider;
  };
  const { menuId } = useSelectedMenu();

  const data = modalIsOpen;

  const emptyForm = () => {
    setName('');
    setSelectedDayOfWeek([]);
    setTimeFrom([dateTimeInitial]);
    setTimeTo([dateTimeInitial]);
  };

  useEffect(() => {
    if (edit && data && typeof data === 'object') {
      setName(data.name);
    } else {
      emptyForm();
    }
  }, [data]);

  async function handleAdd() {
    if (!name || selectedDayOfWeek.length < 1 || !timeFrom || !timeTo)
      return ErrorToast('Please fill in all information');

    // Parse the time values with Luxon
    const timeFromDate = DateTime.fromFormat(
      formatDateToTime24h(timeFrom[0]),
      'HH:mm'
    );
    const timeToDate = DateTime.fromFormat(
      formatDateToTime24h(timeTo[0]),
      'HH:mm'
    );

    // Ensure at least 1 hour difference between "From" and "To"
    const timeDifference = timeToDate.diff(timeFromDate, 'hours').hours;
    if (timeDifference < 1)
      return ErrorToast(
        'There must be at least 1 hour between Time From and Time To'
      );

    const hrs = [] as MenuAvailabilityHourV2Response[];

    if (edit && typeof data === 'object') {
      data?.hours.forEach((element) => {
        return hrs.push({
          day: element.day,
          startTime: element.startTime,
          endTime: element.endTime,
        });
      });
    }

    const bodyEdit: UpdateMenuScheduleV2Dto = {
      name,
      hours: hrs,
    };

    const bodyAdd: UpdateMenuScheduleV2Dto = {
      name,
      hours: selectedDayOfWeek.map((t) => ({
        day: t,
        startTime: formatDateToTime24h(timeFrom[0]),
        endTime: formatDateToTime24h(timeTo[0]),
      })),
    };

    try {
      setLoading?.(true);

      const { data } =
        edit && modalIsOpen && typeof modalIsOpen === 'object'
          ? await updateMenuSchedule(
              locationId,
              brandId,
              modalIsOpen.id,
              bodyEdit
            )
          : await createMenuSchedule(locationId, brandId, bodyAdd, menuId);

      emptyForm();
      closeModal?.();
      setMenu(data?.data);
      SuccessToast(data.message);
      handleChangeShouldPublish(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  const toggleDaySelection = (day: WeekDay) => {
    setSelectedDayOfWeek(
      (prevSelected) =>
        prevSelected.includes(day)
          ? prevSelected.filter((d) => d !== day) // Remove if already selected
          : [...prevSelected, day] // Add if not selected
    );
  };

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`${edit ? 'Edit' : 'Add'} Availability`}
      textAlign="left"
      width="500px"
    >
      <Div $mb="32">
        <AdminInput
          value={name}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
            setName(target.value)
          }
          label="Name"
          placeholder="Enter here"
          $textCenter
        />
      </Div>

      {!edit && (
        <>
          <H5 $center>Select Day</H5>
          <Separator />
          {daysOfWeekStringV2.map(({ name, value }) => (
            <div key={value}>
              <AdminRadioInput
                label={name}
                checked={selectedDayOfWeek.includes(value)}
                onClick={() => toggleDaySelection(value)}
              />
              <Separator />
            </div>
          ))}
          <H5 $mt="32" $mb="16" $center>
            Select Hours From - To
          </H5>
          <Div $flex $gap="24px">
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="From"
              $textCenter
              onChange={setTimeFrom}
              value={timeFrom}
            />
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="To"
              $textCenter
              onChange={setTimeTo}
              value={timeTo}
            />
          </Div>
        </>
      )}

      <Button $selected $w="100%" $mt="32" onClick={() => handleAdd()}>
        Confirm
      </Button>
      <Button $selected $gray $w="100%" $mt="16" onClick={() => closeModal?.()}>
        Cancel
      </Button>
    </Modal>
  );
};
